/**
 * <video-section> Web Component (Optimized, no private methods)
 * Supports MP4, YouTube, Vimeo with lazy loading & autoplay
 */
class VideoSection extends HTMLElement {
  static get observedAttributes() { return ['data-video-url']; }
  static preconnectsAdded = false;

  constructor() {
    super();
    this.videoLoaded = false;
  }

  connectedCallback() {
    if (!VideoSection.preconnectsAdded) this.addPreconnects();
    const deferPriority = this.hasAttribute('data-defer-priority');
    const isPriority = this.hasAttribute('data-priority') || (!deferPriority && this.isFirst());
    isPriority ? this.loadVideo(true) : this.observeVideo();
  }

  disconnectedCallback() { this.observer?.disconnect(); }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'data-video-url' && oldVal !== newVal && this.videoLoaded) this.loadVideo();
  }

  // ---- Helper Methods ----
  isFirst() {
    return document.querySelector('video-section') === this;
  }

  addPreconnects() {
    ['https://www.youtube.com', 'https://i.ytimg.com', 'https://player.vimeo.com']
      .forEach(url => {
        if (!document.querySelector(`link[rel="preconnect"][href="${url}"]`)) {
          const link = Object.assign(document.createElement('link'), {
            rel: 'preconnect', href: url, crossOrigin: 'anonymous'
          });
          document.head.appendChild(link);
        }
      });
    VideoSection.preconnectsAdded = true;
  }

  observeVideo() {
    this.observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !this.videoLoaded) {
        this.loadVideo();
        this.observer.disconnect();
      }
    }, { rootMargin: '50px', threshold: 0.01 });
    this.observer.observe(this);
  }

  extractYouTubeID(url) {
    const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([^&?\/]+)/);
    return match ? match[1] : '';
  }

  createMP4Video(url, priority) {
    const noAutoplay = this.hasAttribute('data-no-autoplay');
    const initHideControls = this.hasAttribute('data-init-hide-controls');
    const video = Object.assign(document.createElement('video'), {
      src: url, autoplay: !noAutoplay, muted: !noAutoplay, playsInline: true, loop: true,
      controls: (this.hasAttribute('data-controls') || noAutoplay) && !initHideControls
    });
    video.style = 'width:100%;height:100%;object-fit:cover;display:block;';
    if (this.dataset.poster) video.poster = this.dataset.poster;
    if (this.dataset.videoClass) video.className = this.dataset.videoClass;
    
    // If controls should be hidden initially, show them on play
    if (initHideControls) {
      video.addEventListener('play', () => {
        video.controls = false;
        const playButton = this.querySelector('.video-play-button');
        if (playButton) playButton.style.display = 'none';
      });
      video.addEventListener('ended', () => {
        const playButton = this.querySelector('.video-play-button');
        if (playButton) playButton.style.display = 'flex';
      });
    }
    
    return video;
  }

  createIframe(url) {
    const noAutoplay = this.hasAttribute('data-no-autoplay');
    const iframe = Object.assign(document.createElement('iframe'), {
      width: '100%', height: '100%', frameBorder: '0',
      allow: 'autoplay; fullscreen; picture-in-picture',
      loading: 'lazy', allowFullscreen: true
    });
    iframe.style = `width:100%;height:100%;border:0;display:block;pointer-events:${noAutoplay ? 'auto' : 'none'};`;
    if (this.dataset.iframeClass) iframe.className = this.dataset.iframeClass;

    let src = '', params;
    if (/youtu/.test(url)) {
      params = new URLSearchParams({
        autoplay: noAutoplay ? 0 : 1, mute: 0,
        controls: this.hasAttribute('data-controls') || noAutoplay ? 1 : 0,
        rel: 0, playsinline: 1, modestbranding: 1, showinfo: 0
      });
      src = `https://www.youtube.com/embed/${this.extractYouTubeID(url)}?${params}`;
    } else if (/vimeo/.test(url)) {
      const id = url.split('/').pop();
      params = new URLSearchParams({
        autoplay: noAutoplay ? 0 : 1, muted: 0,
        background: this.hasAttribute('data-controls') ? 1 : 0,
        rel: 0, playsinline: 1, modestbranding: 1, showinfo: 0
      });
      src = `https://player.vimeo.com/video/${id}?${params}`;
    }
    iframe.src = src;
    return iframe;
  }

  loadVideo(priority = false) {
    if (this.videoLoaded) return;
    const url = this.dataset.videoUrl?.trim();
    if (!url) return console.log('No video URL');

    const baseUrl = url.split('?')[0] || '';
    const isMP4 = baseUrl.toLowerCase().endsWith('.mp4');
    const element = isMP4 ? this.createMP4Video(url, priority) : this.createIframe(url);

    // let wrapper = this.querySelector('.media-wrapper') || Object.assign(document.createElement('div'), {
    //   className: 'media-wrapper',
    //   style: 'width:100%;height:100%;position:relative;'
    // });

    this.innerHTML = '';
    this.append(element);
    // if (!this.contains(wrapper)) this.append(wrapper);

    this.videoLoaded = true;
    this.dataset.loaded = 'true';
    this.dispatchEvent(new CustomEvent('video-loaded', {
      detail: { url, type: isMP4 ? 'mp4' : 'iframe' }
    }));

    const noAutoplay = this.hasAttribute('data-no-autoplay');
    if (isMP4 && priority && !noAutoplay) element.play().catch(() => console.log('Autoplay blocked'));
    
    // Set up play button click handler for MP4 videos
if (isMP4) {
  const playButton = this.querySelector('.video-play-button');
  const element = this.querySelector('video');

  if (playButton && element) {
    playButton.addEventListener('click', () => {
      element.muted = true; // important for iOS autoplay
      element.play().catch((error) => {
        console.log('Video play failed:', error);
      });
    });
  }
}

  }

  // ---- Public Methods ----
  reload() { this.videoLoaded = false; delete this.dataset.loaded; this.loadVideo(true); }
  pause() { this.querySelector('video')?.pause(); }
  play() { this.querySelector('video')?.play().catch(() => {}); }
}

customElements.define('video-section', VideoSection);